import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Header } from '@/components/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, BookOpen, Loader2 } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { Seo } from '@/components/Seo';

const AuthorPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      const { data, error, status } = await apiFetch(`/api/authors/${slug}`);
      if (cancelled) return;
      if (error || !data) {
        if (status === 404) setNotFound(true);
        setAuthor(null);
      } else {
        setAuthor(data);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-16 text-center">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-[#00E5CC]" />
        </div>
      </div>
    );
  }

  if (notFound || !author) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Author not found — ClearCommunicationApp</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Header />
        <div className="container py-16 text-center">
          <h1 className="text-2xl font-bold mb-3">Author not found</h1>
          <p className="text-muted-foreground mb-6">This author profile doesn't exist or is no longer public.</p>
          <Button asChild><Link to="/blog"><BookOpen className="h-4 w-4 mr-2" />Browse blog</Link></Button>
        </div>
      </div>
    );
  }

  const profileUrl = `https://clearcommunicationapp.com/author/${author.slug}`;
  const bio = author.bio || `Contributor at ClearCommunicationApp. ${author.post_count} published article${author.post_count === 1 ? '' : 's'}.`;

  return (
    <div className="min-h-screen bg-background pb-16">
      <Seo
        path={`/author/${author.slug}`}
        title={`${author.name} — Author at ClearCommunicationApp`}
        description={bio}
        image={author.picture || undefined}
        type="profile"
      />
      {/* Schema.org Person + ProfilePage JSON-LD */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            mainEntity: {
              "@type": "Person",
              name: author.name,
              url: profileUrl,
              image: author.picture || undefined,
              description: bio,
              jobTitle: author.is_admin ? "Founder & Author" : "Contributor",
              worksFor: {
                "@type": "Organization",
                name: "ClearCommunicationApp",
                url: "https://clearcommunicationapp.com",
              },
            },
          })}
        </script>
      </Helmet>
      <Header />

      <div className="container py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate('/blog')} className="mb-6" data-testid="author-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            All articles
          </Button>

          {/* Profile header */}
          <div className="flex flex-col sm:flex-row gap-6 items-start mb-10 pb-10 border-b">
            {author.picture ? (
              <img
                src={author.picture}
                alt={author.name}
                className="h-24 w-24 sm:h-32 sm:w-32 rounded-full border-4 border-[#00E5CC]/30 object-cover"
                data-testid="author-avatar"
              />
            ) : (
              <div className="h-24 w-24 sm:h-32 sm:w-32 rounded-full bg-[#00E5CC]/15 border-4 border-[#00E5CC]/30 flex items-center justify-center text-3xl font-bold text-[#00E5CC]">
                {author.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h1 className="text-3xl sm:text-4xl font-bold" data-testid="author-name">{author.name}</h1>
                {author.is_admin && <Badge variant="default" className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00E5CC]">Founder</Badge>}
                {author.is_contributor && !author.is_admin && <Badge variant="secondary">Contributor</Badge>}
              </div>
              {author.author_credentials && (
                <p className="text-sm font-medium text-[#00A896] mb-2" data-testid="author-credentials">
                  {author.author_credentials}
                </p>
              )}
              <p className="text-muted-foreground leading-relaxed" data-testid="author-bio">{bio}</p>
              <div className="mt-3 text-sm text-muted-foreground flex items-center gap-1.5">
                <BookOpen className="h-4 w-4" />
                <span data-testid="author-post-count">{author.post_count} article{author.post_count === 1 ? '' : 's'}</span>
              </div>
            </div>
          </div>

          {/* Posts list */}
          <h2 className="text-xl font-bold mb-4">Articles</h2>
          {author.posts.length === 0 ? (
            <p className="text-muted-foreground">No published articles yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {author.posts.map(p => (
                <Card
                  key={p.post_id || p.slug}
                  className="cursor-pointer hover:border-[#00E5CC] hover:shadow-md transition-all"
                  onClick={() => navigate(`/blog/${p.slug || p.post_id}`)}
                  data-testid={`author-post-${p.slug || p.post_id}`}
                >
                  <CardContent className="p-5">
                    {p.category && (
                      <Badge variant="secondary" className="mb-2 text-[10px]">{p.category}</Badge>
                    )}
                    <h3 className="font-semibold leading-snug mb-2 line-clamp-2">{p.title}</h3>
                    {p.excerpt && (
                      <p className="text-xs text-muted-foreground line-clamp-3 mb-3">{p.excerpt}</p>
                    )}
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {p.published_at
                        ? new Date(p.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                        : 'Draft'}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthorPage;
